import { useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { Translate } from 'next-translate';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import { FEATURE } from '~/common/enums/feature.enum';
import { reportEvent } from '~/domains/analytics';
import { ExternalLink } from '~/domains/common/components/Link/Link';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { bugSnagLog } from '~/utils/bugsnag';
import { UserContext } from '~/components/providers/UserProvider';
import { ContestQueryKeys } from '~/domains/contest/constants/query';
import { updateUserCohort } from '~/services/marketing/marketing.service.api';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import { usePromotionsContext } from '~/domains/promotions/context/PromotionsContext';
import { COHORT_KEYS } from '~/services/marketing/marketing-cohorts.interface';

import { createEntry } from '../../services/entries.service.api';
import type { CurrencyType } from '../../services/enums/currency-type.enum';

const CONTEST_QUERY_KEYS = ['contests', 'entriesForCurrentUser'];

const BEGINNER_CONTEST_ERROR = 'CONTEST_FOR_BEGINNERS_ONLY';

const isBeginnerContestError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.data?.error === BEGINNER_CONTEST_ERROR;

const parseResponseError = (error: unknown, t: Translate) => {
  if (isBeginnerContestError(error)) {
    return {
      title: t('enterContest.error.beginnerOnly.title'),
      message: (
        <Trans
          i18nKey="contest:enterContest.error.beginnerOnly.message"
          components={{
            legalLink: (
              <ExternalLink
                href="https://legal.splashsports.com/legal/splash-sports-house-rules"
                target="_blank"
              />
            ),
          }}
        />
      ),
    };
  }

  return {
    title: t('enterContest.error.default.title'),
    message: t('enterContest.error.default.message'),
  };
};

const useCreateOrJoinEntry = (_contestId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('contest');
  const { user, userCohorts, addUserCohort } = useContext(UserContext);
  const { selectedVoucher, removeVoucher } = usePromotionsContext();
  const voucherId = selectedVoucher?.voucherId;

  const { refetch: getUserBalance } = useWalletBalance();

  const disableDepositAndJoin = !useIsFeatureEnabled(
    FEATURE.ENABLE_CONTEST_DETAILS_JOIN_AND_DEPOSIT_BUTTONS
  );

  // PROTECTION GUARD (KYC, location status, etc)
  const { isLoading: isProtectionLoading, runProtectionChecks } = useProtectedGuard({
    action: 'create an entry',
    protectionLevels: [
      ProtectionGuardType.KYC,
      ProtectionGuardType.LOCATION,
      ProtectionGuardType.GEOCOMPLY,
      ProtectionGuardType.CONTEST,
    ],
    contestId: _contestId,
  });

  const handleJoinThisContestClick = useCallback(
    async ({
      contestId,
      entryFee,
      entriesCount,
      callback,
      suppressError,
      isEntertainmentOnly,
      currencyType,
      zipCode,
    }: {
      contestId: string;
      entryFee: number;
      entriesCount: number;
      callback?: (entryIDs?: string[]) => unknown;
      suppressError?: boolean;
      isEntertainmentOnly?: boolean;
      currencyType?: CurrencyType;
      zipCode?: string;
    }) => {
      if (contestId && !isEntertainmentOnly) {
        const isAllowed = await runProtectionChecks();

        if (!isAllowed) {
          if (!suppressError) {
            throw new Error('Protection Checks Failed');
          }
          return;
        }
      }

      try {
        const entryIDs = await createEntry(
          {
            contestId,
            entriesCount,
            currencyType,
            zipCode,
            voucherId,
          },
          {
            skipLocationValidation: isEntertainmentOnly,
          }
        );

        const isFirstContestJoin = !userCohorts?.includes(COHORT_KEYS.FIRST_CONTEST);

        if (isFirstContestJoin) {
          try {
            await updateUserCohort(COHORT_KEYS.FIRST_CONTEST);
            addUserCohort(COHORT_KEYS.FIRST_CONTEST);
          } catch (cohortError) {
            // Dont want this to fail the entire entry creation if this errors (this will be updated from Segment within 15 minutes anyway if this fails)
            bugSnagLog({
              context: 'Contest First Join Cohort Update',
              severity: 'warning',
              errorMessage: 'Failed to update first contest join cohort',
              errorDetails: { cohortError },
            });
          }

          if (userCohorts?.includes(COHORT_KEYS.FIRST_QP_SUBMIT)) {
            reportEvent('Contest > Played Fantasy & QP');
          }
        }

        const joinEventProperties = {
          contest_id: contestId,
          entry_ids: entryIDs,
          entry_fee: entryFee,
          entries_count: entriesCount,
          ...(zipCode ? { zip_code: zipCode } : {}),
          entry_fee_usd: entryFee / 100,
          af_revenue: entryFee === 0 ? 0 : (entryFee / 100) * entriesCount,
          af_price: entryFee === 0 ? 0 : entryFee / 100,
          voucher_id: voucherId,
        };

        reportEvent(
          isFirstContestJoin ? 'Contest > First Join Now' : 'Contest > Join Now',
          joinEventProperties
        );

        // EDGE-29871: high-value join signal for paid media ($250+ entry fee; entryFee is in cents).
        if (entryFee / 100 >= 250) {
          reportEvent('Contest > Join Now ($250)', joinEventProperties);
        }

        await getUserBalance();

        // The voucher has now been consumed by the backend. Clear it from the
        // selection (and its localStorage-persisted state) so it never reappears
        // as "applied" in checkout. Done here — the single mutation every entry
        // path funnels through — rather than in each caller's success handler.
        if (voucherId) {
          removeVoucher();
        }

        callback?.(entryIDs);
        return entryIDs;
      } catch (entryError) {
        const { title, message } = parseResponseError(entryError, t);

        showNotification({
          title,
          message,
          color: 'red',
        });

        reportEvent('Contest > Join Not Allowed', {
          contest_id: contestId,
          entry_fee: entryFee,
          entry_fee_usd: entryFee / 100,
          entries_count: entriesCount,
          reason: 'API Error',
          error: entryError,
          voucher_id: voucherId,
        });

        bugSnagLog({
          context: 'Contest Join Error',
          severity: 'warning',
          errorMessage: title,
          errorDetails: { entryError },
          details: {
            title,
            message,
            contestId,
            caught: 'Yes',
          },
        });

        if (!suppressError) {
          throw entryError;
        }
      } finally {
        await Promise.all([
          ...CONTEST_QUERY_KEYS.map((queryKey) =>
            Promise.all([
              queryClient.invalidateQueries({ queryKey: [queryKey, contestId, user?.id] }),
            ])
          ),
          queryClient.invalidateQueries(
            ContestQueryKeys.ENTRIES_FOR_CURRENT_USER({ contestId, userId: user?.id })
          ),
        ]);
        await queryClient.invalidateQueries(
          ContestQueryKeys.ENTRIES_CURRENT_USER_WITH_PICKS({ contestId, userId: user?.id })
        );
      }
    },
    [
      runProtectionChecks,
      userCohorts,
      getUserBalance,
      addUserCohort,
      t,
      queryClient,
      user?.id,
      voucherId,
      removeVoucher,
    ]
  );

  const { mutateAsync, isLoading, isSuccess } = useMutation({
    mutationKey: ['createOrJoinEntry'],
    mutationFn: ({
      contestId,
      entryFee,
      entriesCount,
      callback,
      suppressError,
      isEntertainmentOnly,
      currencyType,
      zipCode,
    }: {
      contestId: string;
      entryFee: number;
      entriesCount?: number;
      callback?: (entryIDs: string[]) => unknown;
      suppressError?: boolean;
      isEntertainmentOnly?: boolean;
      currencyType?: CurrencyType;
      zipCode?: string;
    }) =>
      handleJoinThisContestClick({
        contestId,
        entryFee,
        entriesCount,
        callback,
        suppressError,
        isEntertainmentOnly,
        currencyType,
        zipCode,
      }),
  });

  return {
    handleCreateOrJoinContest: mutateAsync,
    isCreateorJoinButtonDisabled: disableDepositAndJoin,
    isLoading: isProtectionLoading || isLoading,
    isSuccess,
  };
};

export default useCreateOrJoinEntry;
